/**
 * Yönetim tanım ekranlarının API istemcisi.
 *
 * Bu dosya BİLEREK küçüktür ve yalnızca dört şey yapar: adres birleştirme,
 * yetki başlığı, hata gövdesinin (§10.3) tipli bir istisnaya çevrilmesi ve
 * kimlik düşmesi durumunda token'ın temizlenmesi.
 *
 * PARA KURALI (ADR-03): bu dosya hiçbir yerde Number()/parseFloat() çağırmaz.
 * Sunucudan gelen para alanları string'dir ve string olarak kalır; ekranda
 * <Tutar> bileşeni, hesapta @kantin/shared'daki para()/paraTopla() kullanılır.
 */
import { HataKodu } from "@kantin/shared";
import { oturumDusmesiniBildir, tokenOku } from "../../veri/api.js";

export { tokenOku } from "../../veri/api.js";

const TABAN = "/api/v1";

/** Sunucunun §10.3 hata gövdesi. */
export interface HataYaniti {
  hata?: string;
  mesaj?: string;
  detay?: Record<string, string>;
}

export class ApiHatasi extends Error {
  readonly kod: string;
  readonly http: number;
  readonly detay: Record<string, string> | undefined;

  constructor(
    http: number,
    kod: string,
    mesaj: string,
    detay?: Record<string, string>,
  ) {
    super(mesaj);
    this.name = "ApiHatasi";
    this.http = http;
    this.kod = kod;
    this.detay = detay;
  }
}

/**
 * KK-1214 / KK-1508: yetki reddi. Sunucu bazı uçlarda 403 yerine bilerek 404
 * döner ("orada bir şey var ama göremiyorsun" bilgisi sızmasın diye), bu
 * yüzden ekranlar yetkisizliği yalnızca HTTP koduna bakarak anlayamaz.
 */
export function yetkisizMi(e: unknown): boolean {
  return (
    e instanceof ApiHatasi &&
    (e.http === 403 || e.kod === HataKodu.YETKISIZ || e.kod === "YETKISIZ")
  );
}

/**
 * KK-1508'in DİĞER yarısı: nokta kapsamı reddi 403 değil, 404/BULUNAMADI
 * döner (eklentiler/kimlik.ts · noktaKapsamiDogrula). `yetkisizMi` bunu
 * bilerek yakalamaz — gerçekten silinmiş bir kaydın 404'ünü "yetkiniz yok"
 * diye göstermek de yanlış olurdu. Yazma uçlarında ise 404'ün ezici çoğunluğu
 * kapsam reddidir; ekran ham "Kayıt bulunamadı." yerine ne yapması
 * gerektiğini söyleyebilsin diye ayrı bir yardımcı veriliyor.
 */
export function kapsamDisiMi(e: unknown): boolean {
  return (
    e instanceof ApiHatasi &&
    e.http === 404 &&
    (e.kod === HataKodu.BULUNAMADI ||
      e.kod === "BULUNAMADI" ||
      e.kod === HataKodu.NOKTA_YETKISIZ)
  );
}

export type SorguParam = Record<
  string,
  string | number | boolean | null | undefined
>;

function adres(yol: string, sorgu?: SorguParam): string {
  if (!sorgu) return `${TABAN}${yol}`;
  const p = new URLSearchParams();
  for (const [anahtar, deger] of Object.entries(sorgu)) {
    if (deger === undefined || deger === null || deger === "") continue;
    p.append(anahtar, String(deger));
  }
  const metin = p.toString();
  return metin ? `${TABAN}${yol}?${metin}` : `${TABAN}${yol}`;
}

function basliklar(jsonMu: boolean, token: string | null): Record<string, string> {
  const h: Record<string, string> = { Accept: "application/json" };
  if (jsonMu) h["Content-Type"] = "application/json";
  if (token) h["Authorization"] = `Bearer ${token}`;
  return h;
}

async function hatayiCoz(yanit: Response, istekTokeni: string | null): Promise<never> {
  let govde: HataYaniti = {};
  try {
    govde = (await yanit.json()) as HataYaniti;
  } catch {
    // Gövdesi olmayan hata (proxy, ağ kesintisi): aşağıdaki varsayılan mesaj.
  }
  if (yanit.status === 401) {
    oturumDusmesiniBildir(istekTokeni);
  }
  throw new ApiHatasi(
    yanit.status,
    govde.hata ?? HataKodu.SUNUCU_HATASI,
    govde.mesaj ??
      (yanit.status === 401
        ? "Oturumunuz sona ermiş. Lütfen tekrar giriş yapın."
        : `İstek başarısız (HTTP ${yanit.status}).`),
    govde.detay,
  );
}

async function govdeCoz<T>(yanit: Response): Promise<T> {
  if (yanit.status === 204) return undefined as T;
  const metin = await yanit.text();
  if (!metin) return undefined as T;
  return JSON.parse(metin) as T;
}

export async function apiOku<T>(yol: string, sorgu?: SorguParam): Promise<T> {
  const token = tokenOku();
  const yanit = await fetch(adres(yol, sorgu), {
    method: "GET",
    headers: basliklar(false, token),
  });
  if (!yanit.ok) return hatayiCoz(yanit, token);
  return govdeCoz<T>(yanit);
}

export async function apiYaz<T>(
  yontem: "POST" | "PATCH" | "PUT" | "DELETE",
  yol: string,
  govde?: unknown,
): Promise<T> {
  const token = tokenOku();
  // Content-Type YALNIZCA gövde varken gönderilir — bkz. veri/api.ts'teki
  // aynı not. Gövdesiz POST'ta başlık kalırsa Fastify 400 döner.
  const yanit = await fetch(adres(yol), {
    method: yontem,
    headers: basliklar(govde !== undefined, token),
    ...(govde === undefined ? {} : { body: JSON.stringify(govde) }),
  });
  if (!yanit.ok) return hatayiCoz(yanit, token);
  return govdeCoz<T>(yanit);
}

/** Çoklu parça yükleme — içe aktarım dosyası (KK-1402). */
export async function apiDosyaYukle<T>(yol: string, dosya: File): Promise<T> {
  const token = tokenOku();
  const govde = new FormData();
  govde.append("dosya", dosya, dosya.name);
  const yanit = await fetch(adres(yol), {
    method: "POST",
    headers: basliklar(false, token),
    body: govde,
  });
  if (!yanit.ok) return hatayiCoz(yanit, token);
  return govdeCoz<T>(yanit);
}

/**
 * Yetki gerektiren dosya indirmesi. Düz bir <a href> Authorization başlığını
 * taşıyamaz; bu yüzden dosya fetch ile alınıp blob olarak indirilir.
 */
export async function apiDosyaIndir(
  yol: string,
  dosyaAdi: string,
  sorgu?: SorguParam,
  yontem: "GET" | "POST" = "GET",
): Promise<void> {
  const token = tokenOku();
  const yanit = await fetch(adres(yol, sorgu), {
    method: yontem,
    headers: { ...basliklar(false, token), Accept: "*/*" },
    cache: "no-store",
  });
  if (!yanit.ok) return hatayiCoz(yanit, token);
  const govde = await yanit.blob();
  if (tokenOku() !== token) {
    throw new Error("Okul veya oturum değişti. Aktif okul için yeniden indirin.");
  }
  const url = URL.createObjectURL(govde);
  const bag = document.createElement("a");
  bag.href = url;
  bag.download = dosyaAdi;
  document.body.appendChild(bag);
  bag.click();
  bag.remove();
  URL.revokeObjectURL(url);
}
